import {
  hasInterestRateChart,
  toDepositTypeId,
} from "../../../../helpers/depositType";
import { validateChart } from "../../../../helpers/interestRateChart";
import {
  TCreateInvestmentProductState,
  TInvestmentProductDetails,
} from "../CreateInvestmentProduct";
import { needsWithdrawalWindows, PlanType } from "./planType";
import { WindowUnit } from "./WithdrawalWindowSection";

/**
 * What a fund still needs before it can be created.
 *
 * These are the rules creation is judged by, checked here so they are met on
 * screen rather than as a 400 with a server message. Each one names the
 * section it belongs to, so the review can offer to take the admin there.
 *
 * Worded for whoever is filling the form in. Nothing here mentions payloads,
 * endpoints or which side of the wire enforces what — none of that is theirs
 * to care about.
 */

export type TReviewProblem = {
  /** The section id, so the review can scroll to it. */
  section: string;
  /** Where it lives, as the admin reads it. */
  where: string;
  message: string;
};

const MAX_INTERVAL: Record<number, number> = {
  [WindowUnit.Daily]: 365,
  [WindowUnit.Weekly]: 52,
  [WindowUnit.Monthly]: 12,
};

const MIN_RATE = 0;
const MAX_RATE = 100;

const UNIT_NAME: Record<number, string> = {
  [WindowUnit.Daily]: "days",
  [WindowUnit.Weekly]: "weeks",
  [WindowUnit.Monthly]: "months",
};

export function reviewFund(
  state: TCreateInvestmentProductState,
): TReviewProblem[] {
  return [
    ...reviewDetails(state),
    ...reviewWindows(state),
    ...state.investmentProductDetails.flatMap(reviewCurrency),
  ];
}

function reviewDetails(state: TCreateInvestmentProductState): TReviewProblem[] {
  const problems: TReviewProblem[] = [];
  const at = (message: string) =>
    problems.push({
      section: "investment-details",
      where: "Investment details",
      message,
    });

  if (!state.name?.trim()) {
    at("The fund has no name");
  }

  if (!state.investmentProductTypeId) {
    at("Investment type is not set");
  }

  if (!state.startDate) {
    at("Subscription opening date is not set");
  }

  if (!state.endDate) {
    at("Subscription closing date is not set");
  }

  if (
    state.startDate &&
    state.endDate &&
    new Date(state.endDate) < new Date(state.startDate)
  ) {
    at("Subscriptions close before they open");
  }

  return problems;
}

function reviewWindows(state: TCreateInvestmentProductState): TReviewProblem[] {
  if (!needsWithdrawalWindows(state.productTypeId)) {
    return [];
  }

  const problems: TReviewProblem[] = [];
  const at = (message: string) =>
    problems.push({
      section: "withdrawal-windows",
      where: "Withdrawal windows",
      message,
    });

  const unit = state.withdrawalWindowIntervalUnit ?? WindowUnit.Monthly;
  const interval = state.withdrawalWindowInterval;
  const max = MAX_INTERVAL[unit];

  if (!interval || interval <= 0) {
    at("How often a window opens is not set");
  } else if (max && interval > max) {
    at(`A window cannot open more than ${max} ${UNIT_NAME[unit]} apart`);
  }

  if (
    state.gracePeriod === null ||
    state.gracePeriod === undefined ||
    state.gracePeriod < 0
  ) {
    at("How long a window stays open is not set");
  }

  return problems;
}

function reviewCurrency(detail: TInvestmentProductDetails): TReviewProblem[] {
  const problems: TReviewProblem[] = [];
  const currency = detail?.currencyCode || detail?.value || "this currency";
  const where = `Financials · ${detail?.label || currency}`;
  const at = (message: string) =>
    problems.push({ section: "financials", where, message });

  if (!detail?.coreBankingTenantId) {
    at("Core banking tenant is not set");
  }

  if (!detail?.coreBankingProductId) {
    at("Core banking product is not set");
  }

  if (!toDepositTypeId(detail?.depositType)) {
    at("Deposit type is not set");
  }

  const min = Number(detail?.minimumInvestment);
  const max = Number(detail?.maximumInvestment);

  if (!min || min <= 0) {
    at("Minimum investment is not set");
  }

  if (!max || max <= 0) {
    at("Maximum investment is not set");
  }

  if (min > 0 && max > 0 && max < min) {
    at("Maximum investment is below the minimum");
  }

  reviewLiquidationRate(
    at,
    "Monthly payout rate",
    detail?.monthlyPrematureLiquidationRate,
  );
  reviewLiquidationRate(
    at,
    "End of tenor rate",
    detail?.endOfTenurePrematureLiquidationRate,
  );

  if (!detail?.investmentInterestPaymentTypeId) {
    at("Interest payment type is not set");
  }

  if (!detail?.termsAndConditionId) {
    at("Terms and conditions is not set");
  }

  if (hasInterestRateChart(detail?.depositType)) {
    const chart = detail?.interestRateChart;

    if (!chart || !chart.chartSlabs?.length) {
      at("The rate chart has no bands");
    } else {
      // The chart's own rules, already stated once. Reported here so the
      // review is the single place an admin looks.
      validateChart(chart).forEach((error) =>
        at(`Rate chart: ${error.message}`),
      );
    }
  }

  return problems;
}

/**
 * An early liquidation penalty, which is a percentage.
 *
 * Zero is a real answer — no penalty — so it is checked for being absent
 * rather than for being falsy. Out of range was a red hint that blocked
 * nothing, which meant the server was the one to say no.
 */
function reviewLiquidationRate(
  at: (message: string) => void,
  label: string,
  rate: number | undefined,
) {
  if (rate === undefined || rate === null || Number.isNaN(Number(rate))) {
    at(`${label} is not set`);

    return;
  }

  if (Number(rate) < MIN_RATE || Number(rate) > MAX_RATE) {
    at(`${label} must be between ${MIN_RATE} and ${MAX_RATE}`);
  }
}

/** True when the fund carries a savings plan type at all. */
export function isSavingsPlan(planTypeId?: number | null): boolean {
  return Object.values(PlanType).includes(Number(planTypeId) as any);
}
